import { useCallback, useEffect, useState } from 'react';
import { AI_CHAT_SETTINGS_MODELS } from '@repo/ai-contract';
import type { AiChatSettings, AiChatSettingsModel } from '@repo/ai-contract';

const STORAGE_KEY = 'ai:chat-settings:v1';

const EMPTY_SETTINGS: AiChatSettings = {};

const VALID_MODELS = new Set<string>(AI_CHAT_SETTINGS_MODELS);

type StoredChatSettings = Omit<AiChatSettings, 'model'> & { readonly model?: unknown };

function isModel(value: string): value is AiChatSettingsModel {
  return VALID_MODELS.has(value);
}

function normalizeModel(raw: unknown): AiChatSettingsModel | undefined {
  if (raw == null) return undefined;
  if (typeof raw !== 'string') return 'default';
  return isModel(raw) ? raw : 'default';
}

function normalizeSettings(settings: StoredChatSettings): AiChatSettings {
  const { model: rawModel, ...rest } = settings;
  const model = normalizeModel(rawModel);
  if (model === undefined) return rest;
  return { ...rest, model };
}

function loadFromStorage(): AiChatSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_SETTINGS;
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return EMPTY_SETTINGS;
    }

    const asSettings = parsed as StoredChatSettings;
    const normalized = normalizeSettings(asSettings);
    if (asSettings.model !== normalized.model) {
      try {
        saveToStorage(normalized);
      } catch {
        // ignore failures to write back
      }
    }
    return normalized;
  } catch {
    return EMPTY_SETTINGS;
  }
}

function saveToStorage(settings: AiChatSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // localStorage full or unavailable — silently ignore
  }
}

/**
 * App-wide AI chat settings persisted in localStorage.
 *
 * Returns the same settings object regardless of whether
 * the caller is global chat or scoped chat.
 * Listens for `storage` events so same-tab panels stay in sync.
 */
export function useAgentChatSettings() {
  const [settings, setSettingsState] = useState<AiChatSettings>(loadFromStorage);

  // Keep in sync if another component on the same tab writes to storage
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setSettingsState(loadFromStorage());
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const updateSettings = useCallback((next: AiChatSettings) => {
    const normalized = normalizeSettings(next);
    setSettingsState(normalized);
    saveToStorage(normalized);
  }, []);

  const resetSettings = useCallback(() => {
    setSettingsState(EMPTY_SETTINGS);
    saveToStorage(EMPTY_SETTINGS);
  }, []);

  return { settings, updateSettings, resetSettings } as const;
}
